import { useEffect, useState } from 'react';

interface MicrosoftAuthResult {
  success: boolean;
  token?: string;
  profile?: {
    username: string;
    uuid: string;
  };
}

/**
 * Page de callback Microsoft OAuth
 * Cette page reçoit le code d'authentification et stocke le token dans localStorage
 */
export const AuthCallback = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Authentification en cours...');

  useEffect(() => {
    const processCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        console.log('❌ Auth error:', error);
        setStatus('error');
        setMessage('La connexion a été annulée ou a échoué.');

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'MS_AUTH_ERROR',
              error,
              source: 'crisp-widget-auth',
            },
            '*'
          );
          console.log('📤 Error sent via postMessage');
        }

        return;
      }

      if (!code) {
        console.log('❌ No code in URL');
        setStatus('error');
        setMessage('Aucun code d\'authentification reçu.');

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'MS_AUTH_ERROR',
              error: 'No auth code received',
              source: 'crisp-widget-auth',
            },
            '*'
          );
          console.log('📤 Error sent via postMessage');
        }

        return;
      }

      console.log('✅ Received auth code, validating...');

      try {
        const response = await fetch(
          `https://login.paladium.games/microsoft/validate?code=${code}&redirect_type=crisp-widget`
        );

        if (!response.ok) {
          throw new Error('Validation failed');
        }

        const data = await response.json() as MicrosoftAuthResult;

        if (data.success && data.token && data.profile) {
          console.log('✅ Microsoft auth successful:', data.profile.username);

          setStatus('success');
          setMessage(`Bienvenue ${data.profile.username} ! Vous pouvez fermer cette page.`);

          console.log('📤 Sending token via postMessage to opener...');

          // window.opener devrait pointer vers l'iframe qui a ouvert cet onglet
          if (window.opener) {
            console.log('✅ window.opener exists');

            try {
              // Envoyer le token à la fenêtre qui a ouvert cet onglet (l'iframe)
              window.opener.postMessage(
                {
                  type: 'MS_AUTH_SUCCESS',
                  token: data.token,
                  source: 'crisp-widget-auth',
                },
                '*' // On accepte tous les origins car c'est le même domaine
              );

              console.log('✅ postMessage sent to window.opener');
              console.log('🔑 Token preview:', data.token.substring(0, 20) + '...');
            } catch (e) {
              console.error('❌ Failed to postMessage:', e);
            }
          } else {
            console.warn('⚠️ window.opener is null - cannot send postMessage');
          }

          console.log('📍 Current window location:', window.location.href);
          console.log('🌐 Current window origin:', window.location.origin);

          // Fermer l'onglet après 2 secondes pour laisser le temps au postMessage
          setTimeout(() => {
            console.log('🚪 Closing auth tab...');
            window.close();
          }, 2000);
        } else {
          throw new Error('Invalid response data');
        }
      } catch (error) {
        console.error('❌ Error during validation:', error);
        setStatus('error');
        setMessage('Erreur lors de la validation. Veuillez réessayer.');

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'MS_AUTH_ERROR',
              error: 'Validation failed',
              source: 'crisp-widget-auth',
            },
            '*'
          );
          console.log('📤 Error sent via postMessage');
        }
      }
    };

    processCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Authentification
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Connexion réussie !
            </h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => window.close()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Fermer cette page
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Erreur
            </h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => window.close()}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Fermer cette page
            </button>
          </>
        )}
      </div>
    </div>
  );
};
